// =============================================================================
// Feature Flags Store — User-Controlled Visibility Layer
// Every user (including Founder) can toggle any feature on/off for their view.
// Flags are persisted in localStorage and optionally synced to the backend.
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type FlagVisibility = 'all' | 'founder' | 'admin' | 'developer' | 'beta';

export interface FeatureFlag {
  id: string;
  label: string;
  description: string;
  group: string;
  defaultVisible: boolean;
  minRole: FlagVisibility;
  isExperimental?: boolean;
  isGlobal?: boolean; // shown in global nav
}

// ── Complete feature flag catalogue ──────────────────────────────────────────
export const FEATURE_FLAG_CATALOGUE: FeatureFlag[] = [
  // Core Platform
  { id: 'dashboard',            label: 'Operations Dashboard',    description: 'Live ops overview.',                   group: 'Core',          defaultVisible: true,  minRole: 'all' },
  { id: 'assets',               label: 'Asset Registry',          description: 'Asset register and IFC import.',       group: 'Core',          defaultVisible: true,  minRole: 'all' },
  { id: 'buildings',            label: 'Building Hierarchy',      description: 'Buildings + Revit linkage.',            group: 'Core',          defaultVisible: true,  minRole: 'all' },
  { id: 'racks',                label: 'Racks & Telecom',         description: 'Rack elevation editor.',                group: 'Core',          defaultVisible: true,  minRole: 'all' },
  { id: 'cabling',              label: 'Cabling',                 description: 'Port-level connectivity graph.',        group: 'Core',          defaultVisible: true,  minRole: 'all' },
  { id: 'projects',             label: 'Projects',                description: 'Project lifecycle management.',         group: 'Core',          defaultVisible: true,  minRole: 'all' },
  { id: 'documents',            label: 'Documents',               description: 'Document register.',                   group: 'Core',          defaultVisible: true,  minRole: 'all' },
  { id: 'schedule',             label: 'Schedule',                description: 'Calendar and milestone tracking.',      group: 'Core',          defaultVisible: true,  minRole: 'all' },
  { id: 'clashes',              label: 'Clash Detection',         description: 'BIM clash detection.',                 group: 'Core',          defaultVisible: true,  minRole: 'all' },
  // Intelligence
  { id: 'mie',                  label: 'MIE 2.0',                 description: 'Multimedia Intelligence Engine.',      group: 'Intelligence',  defaultVisible: true,  minRole: 'all' },
  { id: 'r2-voice',             label: 'R2 Voice',                description: 'Conversational voice interface.',      group: 'Intelligence',  defaultVisible: true,  minRole: 'all' },
  { id: 'r2-brain',             label: 'R2 Brain',                description: 'Intelligence Command Centre.',         group: 'Intelligence',  defaultVisible: true,  minRole: 'all' },
  { id: 'job-queue',            label: 'Job Queue & Media',       description: 'Background render engine.',            group: 'Intelligence',  defaultVisible: true,  minRole: 'all' },
  { id: 'ai',                   label: 'Emmy — Eng. Advisor',     description: 'Local AI assistant.',                  group: 'Intelligence',  defaultVisible: true,  minRole: 'all',  isExperimental: true },
  { id: 'r2-academy',           label: 'R2 Academy',              description: 'Training assistant.',                  group: 'Intelligence',  defaultVisible: true,  minRole: 'all' },
  { id: 'sie',                  label: 'Smart Ingest (SIE)',      description: 'Smart Ingestion Engine.',              group: 'Intelligence',  defaultVisible: true,  minRole: 'all' },
  // EOS Modules
  { id: 'bim-project-config',   label: 'New BIM Project',         description: 'BIM project configuration.',          group: 'EOS',           defaultVisible: true,  minRole: 'all' },
  { id: 'bim-engine',           label: 'R² Generation Engine',   description: 'AI BIM generation engine.',            group: 'EOS',           defaultVisible: true,  minRole: 'all',  isExperimental: true },
  { id: 'marketplace',          label: 'Model Marketplace',       description: '3D model marketplace.',               group: 'EOS',           defaultVisible: true,  minRole: 'all' },
  // Finance
  { id: 'estimator',            label: 'RCD² Estimator',         description: 'Cost estimation engine.',              group: 'Finance',       defaultVisible: true,  minRole: 'all' },
  { id: 'finance',              label: 'Personal Finance',        description: 'Financial intelligence.',              group: 'Finance',       defaultVisible: true,  minRole: 'all' },
  { id: 'finance-pro',          label: 'Business Finance',        description: 'Corporate finance.',                   group: 'Finance',       defaultVisible: true,  minRole: 'all' },
  // Experimental / Founder-only
  { id: 'bim-operator-dashboard', label: 'Operator Dashboard',   description: 'BIM ops admin panel.',                group: 'Admin',         defaultVisible: false, minRole: 'admin', isExperimental: true },
  { id: 'marketplace-operator',  label: 'Marketplace Ops',       description: 'Marketplace operator panel.',          group: 'Admin',         defaultVisible: false, minRole: 'admin', isExperimental: true },
  { id: 'founder-approvals',    label: 'Founder Approvals',      description: 'Approval inbox.',                      group: 'Founder',       defaultVisible: true,  minRole: 'founder' },
  { id: 'investor-relations',   label: 'Investor Relations',     description: 'Investor CRM.',                        group: 'Founder',       defaultVisible: true,  minRole: 'founder' },
  { id: 'marketing-controls',   label: 'Marketing & Display',    description: 'Marketing controls.',                  group: 'Founder',       defaultVisible: true,  minRole: 'founder' },
  { id: 'global-command',       label: 'Global Command Centre',  description: 'Master admin command centre.',         group: 'Founder',       defaultVisible: true,  minRole: 'founder' },
  { id: 'global-map',           label: 'Global Map',             description: 'Geographic project distribution.',     group: 'Founder',       defaultVisible: false, minRole: 'founder', isExperimental: true },
  { id: 'feature-flags',        label: 'Feature Flag Manager',   description: 'Toggle features on/off.',             group: 'Founder',       defaultVisible: true,  minRole: 'founder' },
  // System
  { id: 'settings',             label: 'Settings',               description: 'Workspace preferences.',              group: 'System',        defaultVisible: true,  minRole: 'all' },
  { id: 'pricing',              label: 'License & Subscription', description: 'Subscription management.',            group: 'System',        defaultVisible: true,  minRole: 'all' },
  { id: 'help',                 label: 'Support & Feedback',     description: 'Support and feedback.',               group: 'System',        defaultVisible: true,  minRole: 'all' },
  { id: 'sync',                 label: 'Sync Engine',            description: 'Local + cloud sync queue.',           group: 'System',        defaultVisible: true,  minRole: 'all' },
  { id: 'health',               label: 'Health Monitoring',      description: 'System health probes.',               group: 'System',        defaultVisible: true,  minRole: 'all' },
  { id: 'backup',               label: 'Backups',                description: 'Encrypted local backups.',            group: 'System',        defaultVisible: true,  minRole: 'all' },
];

interface FeatureFlagsState {
  // User flag overrides: { featureId: boolean } — true=show, false=hide
  overrides: Record<string, boolean>;
  // Check if a feature is visible for the current user
  isVisible: (id: string, isFounder: boolean, isMaster: boolean) => boolean;
  // Toggle a specific flag
  toggle: (id: string) => void;
  // Set a flag explicitly
  setFlag: (id: string, value: boolean) => void;
  // Reset all to defaults
  resetAll: () => void;
  // Reset one flag
  resetFlag: (id: string) => void;
}

export const useFeatureFlagsStore = create<FeatureFlagsState>()(
  persist(
    (set, get) => ({
      overrides: {},

      isVisible: (id: string, isFounder: boolean, isMaster: boolean): boolean => {
        const entry = FEATURE_FLAG_CATALOGUE.find((f) => f.id === id);
        if (!entry) return true; // unknown features default to visible

        // Check role requirement first
        if (entry.minRole === 'founder' && !isFounder && !isMaster) return false;
        if (entry.minRole === 'admin'   && !isMaster)                return false;

        // Then check user override
        const overrides = get().overrides;
        if (id in overrides) return Boolean(overrides[id]);

        return entry.defaultVisible;
      },

      toggle: (id: string) => {
        set((s) => {
          const entry = FEATURE_FLAG_CATALOGUE.find((f) => f.id === id);
          const current = id in s.overrides ? s.overrides[id] : (entry?.defaultVisible ?? true);
          return { overrides: { ...s.overrides, [id]: !current } };
        });
      },

      setFlag: (id: string, value: boolean) => {
        set((s) => ({ overrides: { ...s.overrides, [id]: value } }));
      },

      resetAll: () => set({ overrides: {} }),

      resetFlag: (id: string) => {
        set((s) => {
          const next = { ...s.overrides };
          delete next[id];
          return { overrides: next };
        });
      },
    }),
    { name: 'r2.feature-flags' },
  ),
);
